'use client';

import React, { Component, ReactNode } from 'react';
import { ErrorHandler, AppError } from '@repo/errors';
import { clientLogger } from '@repo/observability/client';
import { Button } from '@repo/ui';

interface Props {
  children: ReactNode;
  fallback?: (error: AppError, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: AppError | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: unknown): State {
    // Convert the error to AppError using ErrorHandler
    const appError = ErrorHandler.normalize(error);
    
    // Log the error
    ErrorHandler.log(appError);
    
    return {
      hasError: true,
      error: appError
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Additional logging if needed
    console.error('Error boundary caught:', error, errorInfo);
    void clientLogger.error(error, {
      componentName: 'ErrorBoundary',
      action: 'componentDidCatch',
      feature: 'unknown',
      handled: true,
      metadata: { componentStack: errorInfo.componentStack },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
            <div className="text-center">
              <div className="mb-4">
                <svg
                  className="mx-auto h-12 w-12 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                {this.state.error.isOperational ? 'Something went wrong' : 'Unexpected error'}
              </h2>
              
              <p className="text-sm text-gray-600 mb-6">
                {ErrorHandler.getUserMessage(this.state.error)}
              </p>

              {/* Show error code in development */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mb-4 p-3 bg-gray-100 rounded text-xs text-gray-700 text-left">
                  <p className="font-mono">
                    Code: {this.state.error.code}
                    <br />
                    Status: {this.state.error.statusCode}
                  </p>
                </div>
              )}

              <div className="flex gap-3 justify-center">
                <Button
                  onClick={this.handleReset}
                  variant="primary"
                  size="sm"
                >
                  Try Again
                </Button>
                
                <Button
                  onClick={() => window.location.href = '/'}
                  variant="outline"
                  size="sm"
                >
                  Go Home
                </Button>
              </div>

              {/* Retry indicator for retriable errors */}
              {ErrorHandler.isRetriable(this.state.error) && (
                <p className="mt-4 text-xs text-gray-500">
                  This error is temporary. Please try again in a moment.
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook for using error boundary with custom error handling
export function useErrorHandler() {
  return (error: unknown) => {
    const appError = ErrorHandler.normalize(error);
    ErrorHandler.log(appError);
    
    // You could also trigger notifications here
    // For now, just throw to be caught by boundary
    throw appError;
  };
}